import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Spin,
  Row,
  Col,
  Modal,
  message,
} from 'antd';

import {
  SearchOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import Table from 'components/Table';
import { getVersionesVehiculares } from 'api/catalogos';
import Select from './Select';

const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';
const fields = ['tipo_de_vehiculo_en_version', 'marca_en_version', 'linea_en_version', 'descripcion'];

const BusquedaAvanzadaVersion = ({
  visible,
  setVisible,
  callback,
  dataExterno,
  setDataExterno,
  linea,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (linea) form.setFieldsValue({ linea_vehicular: linea.id });
  }, [linea]);

  const onCancel = () => {
    setVisible(false);
    setData([]);
    if (setDataExterno) {
      setDataExterno([]);
    }
    setSelectedRowKeys([]);
    form.resetFields(fields);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      setSelectedRowKeys([]);
      const params = form.getFieldsValue();
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);

      if (validProps.length) {
        const response = await getVersionesVehiculares({
          page: 1,
          ...params,
        });
        setCurrentPage(1);
        if (response?.results) {
          setData(response);
        } else {
          message.warn('No se encontro Version', 2.5);
          setData([]);
        }
      } else {
        message.warn('Debe ingresar al menos un campo');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onFinishSearch = async (record) => {
    callback(record);
    onCancel();
  };

  const fetch = async (page = 1) => {
    const params = form.getFieldsValue();
    setLoading(true);
    setCurrentPage(page);
    const _data = await getVersionesVehiculares({
      ...params,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const handleLocalSearch = () => {
    const values = form.getFieldsValue();
    const keys = Object.keys(values);
    const validProps = keys.filter((prop) => values[prop]);
    if (validProps.length) {
      const normalizeText = (text = '') => text?.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      const validateIncludes = (obj, prop) => {
        if (validProps.includes(prop)) {
          const normalizedValue = normalizeText(obj[prop]);
          const normalizedInput = normalizeText(values[prop]);
          return normalizedValue.includes(normalizedInput);
        }
        return false;
      };

      const validateEquals = (obj, prop) => obj[prop] === values[prop];
      const filtered = data.filter((item) => validateIncludes(item, 'ciudadano')
        || validateEquals(item, 'uso_de_predio'));
      setData(filtered);
    } else {
      message.warn('Debe ingresar al menos un campo');
    }
  };

  useEffect(() => {
    setData(dataExterno);
    // eslint-disable-next-line
  }, [dataExterno]);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const add = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const match = data.results.find((e) => e.id === key);
      onFinishSearch(match);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  const controls = [
    {
      text: 'Regresar',
      icon: <ArrowLeftOutlined />,
      onClick: () => onCancel(),
    },
    {
      text: 'Limpiar',
      icon: <DeleteOutlined />,
      onClick: () => {
        setData([]);
        form.resetFields(fields);
      },
    },
  ];

  const columns = [
    !REPUVE && {
      titleText: 'Tipo de Vehículo',
      dataIndex: 'tipo_de_vehiculo_en_version',
      key: 'tipo_de_vehiculo_en_version',
      width: 300,
    },
    !REPUVE && {
      titleText: 'Marca',
      dataIndex: 'marca_en_version',
      key: 'marca_en_version',
      width: 300,
    },
    !REPUVE && {
      titleText: !REPUVE ? 'Línea' : 'Submarca',
      dataIndex: 'linea_en_version',
      key: 'linea_en_version',
      width: 300,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
  ].filter((e) => e);

  return (
    <>
      <Modal
        width="90%"
        visible={visible}
        closable={false}
        footer={null}
        title={(
          <Row justify="space-between">
            Búsqueda Avanzada
            <FormSubmitControls
              controls={controls}
              loading={loading}
            />
          </Row>
        )}
        onCancel={onCancel}
        maskClosable={!loading}
        keyboard={!loading}
        forceRender
        centered
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={dataExterno?.length ? handleLocalSearch : handleSearch}
          >
            <Row gutter={10}>
              {!REPUVE && (
                <>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="tipo_de_vehiculo_en_version"
                      label="Tipo de Vehículo"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="marca_en_version"
                      label="Marca"
                      hasFeedback
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                </>
              )}
              {!REPUVE ? (
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="linea_en_version"
                    label={!REPUVE ? 'Línea' : 'Submarca'}
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="linea_vehicular"
                    label="Submarca"
                    hasFeedback
                  >
                    <Select dataSource={[linea]} labelProp="nombre" disabled />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  hasFeedback
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 5]} justify="end">
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button block htmlType="submit">
                  <SearchOutlined />
                  Buscar
                </Button>
              </Col>
              <Col xs={24} sm={24} md={6} lg={4}>
                <Button
                  onClick={add}
                  block
                  disabled={!selectedRowKeys.length}
                >
                  <PlusOutlined />
                  Cargar
                </Button>
              </Col>
            </Row>
          </Form>
          <Col span={24}>
            <Table
              cols={columns}
              data={data?.results}
              noControls
              allowSort={false}
              allowSearch={false}
              rowSelection={rowSelection}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedRowKeys([record.id]);
                },
                onDoubleClick: add,
              })}
              currentPage={currentPage}
              rowKey="id"
              pagination={false}
              paginationCallback={fetch}
              count={data?.count}
              mobileColIndex={REPUVE ? 0 : 1}
            />
          </Col>
        </Spin>
      </Modal>
    </>
  );
};

BusquedaAvanzadaVersion.propTypes = {
  callback: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  dataExterno: PropTypes.arrayOf(PropTypes.shape()),
  setDataExterno: PropTypes.func,
  linea: PropTypes.shape({ id: PropTypes.number.isRequired }),
};

BusquedaAvanzadaVersion.defaultProps = {
  dataExterno: [],
  setDataExterno: null,
  linea: null,
};

export default BusquedaAvanzadaVersion;
